/* Codex LP only: retain the theme product form and its cart/error UI.
   Shared product-form.js is unchanged. Never retry an uncertain cart request. */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // A lost add response in either LP purchase flow locks this page until
        // the shopper reviews the cart. Never clear this flag on pageshow.
        this.onCartUncertain = this.lockCartUncertain.bind(this);
        document.addEventListener('fsv:cart-uncertain', this.onCartUncertain);
        window.addEventListener('pageshow', this.onCartUncertain);
        if (window.__fsvCartUncertain === true) this.lockCartUncertain();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (window.__fsvCartUncertain === true || this.dataset.fsvCartUncertain === 'true') {
          this.lockCartUncertain();
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;
        this.cartAddConfirmed = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.requestCart(config, formData.get('id'))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || 'カートに追加できませんでした。内容をご確認ください。');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.cartAddConfirmed = true;
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            // A lost response can still mean Shopify accepted the add. Keep the
            // same form disabled until the customer checks their cart/reloads.
            const message = 'カートへの追加結果を確認できませんでした。すでに追加されている可能性があります。カートの内容をご確認ください。';
            window.__fsvCartUncertain = true;
            document.dispatchEvent(new Event('fsv:cart-uncertain'));
            this.error = true;
            this.dataset.fsvCartUncertain = 'true';
            this.submitButton.disabled = true;
            this.handleErrorMessage(message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: message,
              message: message,
            });
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cartAddConfirmed && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      lockCartUncertain() {
        if (window.__fsvCartUncertain !== true && this.dataset.fsvCartUncertain !== 'true') return;
        window.__fsvCartUncertain = true;
        this.dataset.fsvCartUncertain = 'true';
        this.error = true;
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.handleErrorMessage('追加結果を確認できませんでした。すでに追加されている可能性があります。カートの内容をご確認ください。');
      }

      requestCart(config, variantId) {
        const controller = new AbortController();
        config.signal = controller.signal;
        let timer;
        const request = fetch(`${routes.cart_add_url}`, config).then(async (response) => {
          const data = await response.json();
          // Shopify's explicit 4xx rejection uses the existing form error path.
          if (!response.ok && response.status >= 400 && response.status < 500 && data && data.status) {
            return data;
          }
          if (!response.ok) throw new Error('Cart HTTP failure');
          if (!data || typeof data !== 'object') throw new Error('Invalid cart response');
          if (data.status) throw new Error('Unexpected cart error response');
          if (String(data.variant_id || data.id) !== String(variantId) ||
              !Number.isInteger(data.quantity) || data.quantity < 1 ||
              !Number.isInteger(data.price) || data.price < 0 ||
              (this.cart && (!data.sections || typeof data.sections !== 'object' ||
                this.cart.getSectionsToRender().some((section) => typeof data.sections[section.id] !== 'string')))) {
            throw new Error('Invalid cart response');
          }
          return data;
        });
        const timeout = new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            reject(new Error('Cart response timeout'));
            controller.abort();
          }, 10000);
        });
        return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.dataset.fsvErrorText = String(errorMessage);
          this.errorMessage.textContent = errorMessage;
          if (this.dataset.fsvCartUncertain === 'true') {
            const cartLink = document.createElement('a');
            cartLink.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart';
            cartLink.textContent = 'カートの内容を確認する';
            cartLink.style.cssText = 'display:block;color:inherit;text-decoration:underline;padding:12px 0;';
            this.errorMessage.appendChild(cartLink);
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          if (window.__fsvCartUncertain === true) { this.lockCartUncertain(); return; }
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

